import { CloseScrollStrategy, Overlay } from '@angular/cdk/overlay';
import { AsyncPipe, NgForOf, NgIf } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
  MatAutocompleteModule,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { MatErrorMessagesDirective, UppercaseDirective } from 'app/shared/directives';
import { Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith } from 'rxjs/operators';

export interface IAutocompleteItem<T> {
  label: string;
  value: T;
  disabled?: boolean;
  sublabel?: string;
}

export function scrollFactory(overlay: Overlay): () => CloseScrollStrategy {
  return () => overlay.scrollStrategies.close();
}

@Component({
  selector: 'app-input-autocomplete',
  templateUrl: './input-autocomplete.component.html',
  providers: [
    {
      provide: MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
      useFactory: scrollFactory,
      deps: [Overlay],
    },
  ],
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    TranslateModule,
    MatIcon,
    MatAutocompleteModule,
    NgIf,
    AsyncPipe,
    MatOptionModule,
    NgForOf,
    MatErrorMessagesDirective,
    MatInputModule,
    MatButtonModule,
    UppercaseDirective,
  ],
})
export class InputAutocompleteComponent<T = unknown> implements OnInit, OnChanges {
  protected readonly elementRef = inject(ElementRef);

  @Output() public optionSelected = new EventEmitter<IAutocompleteItem<T> | null>();

  @Input() public controlName!: string;
  @Input() public form!: FormGroup;
  @Input() public aria!: string;
  @Input() public onlyUppercase = false;

  @Input({ transform: (value: IAutocompleteItem<string>[] | null) => value ?? [] })
  public items: IAutocompleteItem<T>[] = [];
  @Input() public selectedItem: IAutocompleteItem<T> | null = null;
  @Input() public labelKey = '';

  // set to true to allow item not in list to be entered as a value
  @Input() public allowNew = false;

  @ViewChild(MatAutocompleteTrigger)
  private autoTrigger!: MatAutocompleteTrigger;
  @ViewChild('inputRef') public inputRef!: ElementRef<HTMLInputElement>;

  private destroyRef = inject(DestroyRef);

  public filteredItems!: Observable<IAutocompleteItem<T>[]>;

  public get inputControl(): AbstractControl | null {
    return this.form.get(this.controlName);
  }

  private initialized = false; // Track if component has been initialized

  public ngOnInit(): void {
    this.setupFilteredItems();

    this.inputControl?.valueChanges
      ?.pipe(debounceTime(400), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((v) => {
        this.allowEnteredValue(v);
      });
  }

  public allowEnteredValue(v: string): void {
    const foundItems = this.filterAutocomplete(v);

    if (!foundItems?.length && !this.allowNew) {
      this.inputControl?.setValue(null);
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']?.currentValue) {
      this.setupFilteredItems();
    }
    if (changes['selectedItem']?.currentValue) {
      const selectedItem = changes['selectedItem'].currentValue;
      if (selectedItem) {
        this.inputControl?.setValue(selectedItem.value, { emitEvent: false });
      }
    }
  }

  private setupFilteredItems(): void {
    if (this.inputControl) {
      this.filteredItems = this.inputControl.valueChanges.pipe(
        startWith(''),
        map((value) => (typeof value === 'string' ? value : this.getLabelForValue(value))),
        map((label) => (label ? this.filterAutocomplete(label) : this.items.slice())),
        takeUntilDestroyed(this.destroyRef),
      );
    } else {
      this.filteredItems = of(this.items.slice());
    }
  }

  public displayFn = (value: T | null): string => {
    const matchingItems = this.items.filter((item) => item.value === value);
    if (matchingItems.length > 1) {
      console.warn('Multiple items found for value', value);
      const matchingItem = this.items.find((item) => item.label === this.selectedItem?.label);
      return matchingItem ? matchingItem.label : '';
    }
    const [matchingItem] = matchingItems;
    return matchingItem ? matchingItem.label : '';
  };

  public onBlur(): void {
    const exactMatch = this.items.find((item) => item.label === this.inputControl?.value);
    if (exactMatch?.disabled) {
      this.inputControl?.setValue(null);
    }
  }

  public onFocus(): void {
    if (this.initialized && this.autoTrigger) {
      this.autoTrigger.openPanel();
    }
    this.setupFilteredItems();
  }

  public onAutocompleteClosed(): void {
    if (
      !this.allowNew &&
      !this.items.find((x) => x.value === this.inputControl?.value || x.label === this.inputControl?.value)
    ) {
      this.inputControl?.setValue(null);
      this.onOptionSelected(null);
    }
  }

  public clearInput(): void {
    this.inputControl?.setValue(null);
    this.onOptionSelected(null);
    this.inputControl?.updateValueAndValidity();
  }

  public onOptionSelected(option: IAutocompleteItem<T> | null): void {
    this.selectedItem = option;
    this.optionSelected.emit(option);
    setTimeout(() => {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }, 100);
  }

  private filterAutocomplete(label: string): IAutocompleteItem<T>[] {
    return this.items.filter((item) => {
      return (
        item?.label?.toLowerCase().includes(label?.toLowerCase()) ||
        String(item?.value || '')
          .toLowerCase()
          .includes(label?.toLowerCase())
      );
    });
  }

  private getLabelForValue(value: T): string {
    const matchingItem = this.items.find((item) => item.value === value);
    return matchingItem ? matchingItem.label : '';
  }

  private closeAutocomplete(): void {
    if (this.autoTrigger && this.autoTrigger.panelOpen) {
      this.autoTrigger.closePanel();
    }
  }

  @HostListener('document:touchstart', ['$event'])
  public onDocumentTouch(event: TouchEvent): void {
    this.handleAutocompleteOverlay(event);
  }

  @HostListener('document:scroll', ['$event'])
  public onDocumentScroll(event: Event): void {
    this.handleAutocompleteOverlay(event);
  }

  private handleAutocompleteOverlay(event: Event): void {
    const autocompletePanel = document.querySelector('.mat-mdc-autocomplete-panel');
    if (
      !this.elementRef.nativeElement.contains(event.target) &&
      !(autocompletePanel && autocompletePanel.contains(event.target as Node))
    ) {
      this.closeAutocomplete();
    }
  }
}
